import { useCallback, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  AuthRequired,
  AuthRequiredProps,
  ChartThemeProvider,
  ComponentFallback,
} from "@jana-technology/platform-ui";
import { captureException, ErrorBoundary } from "@sentry/react";
import { ApolloClient } from "@apollo/client";
import { ApolloProvider } from "@apollo/client/react";

import { makeApolloClient } from "$/apollo";
import { loginWithAuthMS } from "api/auth-ms";
import { auth0Client } from "auth/auth0-client";
import { LOGIN_ROUTE, LOGOUT_ROUTE } from "constants/routing";
import { ExportProvider } from "contexts/export/ExportProvider";
import { GlobalFiltersProvider } from "contexts/filters";
import { callWithExponentialBackoff } from "helpers/exp-backoff";

const fallback = () => <ComponentFallback />;

export const Home = () => {
  const { pathname, search } = useLocation();
  const navigate = useNavigate();

  // do not call auth microservice on route changes (pathname dependency)
  const [authedWithMS, setAuthedWithMS] = useState(false);

  const [apolloClient, setApolloClient] = useState<ApolloClient | null>(null);

  const onAuthentication = useCallback(() => {
    navigate(LOGIN_ROUTE, {
      state: {
        returnTo: `${pathname}${search}`,
      },
    });
  }, [navigate, pathname, search]);

  const callGateway = useCallback<
    NonNullable<AuthRequiredProps["callGateway"]>
  >(
    async (signal) => {
      // do not call auth on the home page since it's always rerouted to DEFAULT_ROUTE
      if (pathname === "/") {
        return;
      }

      if (authedWithMS) {
        return;
      }

      let response;

      try {
        const token = await auth0Client.getTokenSilently();
        const user = await auth0Client.getUser();

        if (token && user) {
          response = await callWithExponentialBackoff(async () =>
            loginWithAuthMS(user, token, signal),
          );

          if (!response.ok) {
            throw new Error(`callGateway returned ${response.status}`);
          }

          const _apolloClient = makeApolloClient(token);

          // helps with testing to ensure no cached data is present
          // should help with stale data after login/logout as well
          await _apolloClient.resetStore();

          setAuthedWithMS(true);
          setApolloClient(_apolloClient);
        }
      } catch (error) {
        const _error = error as Error;

        captureException(_error, {
          tags: {
            component: "Home",
            caller: "callGateway",
          },
        });

        /*
         If the auth ms failed, log the user out and ask to log in.

         Ignore AbortController cancelled calls since redirecting (mainly from
         / to DEFAULT_ROUTE) triggers an effect on onAuthentication [pathname].
         */
        if (
          import.meta.env.LH_ENV_NAME !== "localhost" &&
          _error.name !== "AbortError"
        ) {
          captureException(error, {
            tags: {
              component: "Home",
              caller: "callGateway",
            },
          });

          navigate(LOGOUT_ROUTE);
        }
      }
    },
    [navigate, authedWithMS, pathname],
  );

  return (
    <ErrorBoundary fallback={fallback}>
      <AuthRequired
        onAuthentication={onAuthentication}
        callGateway={callGateway}
      >
        <ChartThemeProvider>
          {apolloClient && (
            <ApolloProvider client={apolloClient}>
              <ExportProvider>
                <GlobalFiltersProvider>
                  <Outlet />
                </GlobalFiltersProvider>
              </ExportProvider>
            </ApolloProvider>
          )}
        </ChartThemeProvider>
      </AuthRequired>
    </ErrorBoundary>
  );
};

export default Home;
